import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PWAService, PWAInstallInfo } from '../../../core/pwa.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pwa-install-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Banner de Instalação PWA -->
    <div
      *ngIf="showBanner && !installInfo.isInstalled"
      class="pwa-install-banner fixed bottom-4 left-4 right-4 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-2xl transform transition-all duration-500"
      [class.translate-y-0]="showBanner"
      [class.translate-y-full]="!showBanner"
    >
      <div class="p-4">
        <!-- Header do Banner -->
        <div class="flex items-start justify-between mb-3">
          <div class="flex items-center">
            <div
              class="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-3"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-lg">Instalar Estokai</h3>
              <p class="text-blue-100 text-sm">Acesso rápido e offline</p>
            </div>
          </div>
          <button
            (click)="dismissBanner()"
            class="text-white/70 hover:text-white transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <!-- Benefícios -->
        <div class="grid grid-cols-2 gap-2 mb-4 text-sm">
          <div class="flex items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="mr-2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12l2 2 4-4" />
            </svg>
            <span>Funciona offline</span>
          </div>
          <div class="flex items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="mr-2"
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="10,8 16,12 10,16 10,8" />
            </svg>
            <span>Mais rápido</span>
          </div>
          <div class="flex items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="mr-2"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span>Notificações</span>
          </div>
          <div class="flex items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="mr-2"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9,22 9,12 15,12 15,22" />
            </svg>
            <span>Tela inicial</span>
          </div>
        </div>

        <!-- Botões de Ação -->
        <div class="flex gap-2">
          <button
            (click)="installApp()"
            [disabled]="isInstalling"
            class="flex-1 bg-white text-blue-600 font-semibold py-2.5 px-4 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span
              *ngIf="!isInstalling"
              class="flex items-center justify-center"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="mr-2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7,10 12,15 17,10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Instalar
            </span>
            <span *ngIf="isInstalling" class="flex items-center justify-center">
              <div
                class="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"
              ></div>
              Instalando...
            </span>
          </button>
          <button
            (click)="showInstructions = !showInstructions"
            class="bg-white/20 text-white px-4 py-2.5 rounded-lg hover:bg-white/30 transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        </div>

        <!-- Instruções Expandidas -->
        <div
          *ngIf="showInstructions"
          class="mt-4 p-3 bg-black/20 rounded-lg text-sm animate-in slide-in-from-top duration-300"
        >
          <h4 class="font-semibold mb-2 flex items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="mr-2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Como instalar:
          </h4>
          <p class="text-blue-100">{{ installInstructions }}</p>
        </div>
      </div>
    </div>

    <!-- Toast de Instalação -->
    <div
      *ngIf="showInstallToast"
      class="fixed top-4 left-4 right-4 z-50 bg-green-500 text-white p-4 rounded-xl shadow-lg transform transition-all duration-500"
    >
      <div class="flex items-center">
        <div
          class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="20,6 9,17 4,12" />
          </svg>
        </div>
        <div class="flex-1">
          <h4 class="font-semibold">App instalado com sucesso!</h4>
          <p class="text-green-100 text-sm">
            Estokai agora está na sua tela inicial
          </p>
        </div>
        <button
          (click)="showInstallToast = false"
          class="text-white/70 hover:text-white transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- Update disponível -->
    <div
      *ngIf="updateAvailable"
      class="fixed top-4 left-4 right-4 z-50 bg-amber-500 text-white p-4 rounded-xl shadow-lg"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div
            class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="23,4 23,10 17,10" />
              <polyline points="1,20 1,14 7,14" />
              <path
                d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
              />
            </svg>
          </div>
          <div>
            <h4 class="font-semibold">Atualização disponível</h4>
            <p class="text-amber-100 text-sm">Nova versão do Estokai</p>
          </div>
        </div>
        <div class="flex gap-2">
          <button
            (click)="updateApp()"
            class="bg-white text-amber-600 font-semibold px-3 py-1.5 rounded-lg text-sm hover:bg-amber-50 transition-colors"
          >
            Atualizar
          </button>
          <button
            (click)="dismissUpdate()"
            class="text-white/70 hover:text-white transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .pwa-install-banner {
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }

      @keyframes slide-in-from-top {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .animate-in {
        animation-fill-mode: both;
      }

      .slide-in-from-top {
        animation-name: slide-in-from-top;
      }

      @media (min-width: 768px) {
        .pwa-install-banner {
          left: auto;
          right: 1rem;
          max-width: 380px;
        }
      }
    `,
  ],
})
export class PWAInstallBannerComponent implements OnInit, OnDestroy {
  private pwaService = inject(PWAService);
  private destroy$ = new Subject<void>();

  showBanner = false;
  showInstructions = false;
  showInstallToast = false;
  isInstalling = false;
  updateAvailable = false;
  installInfo: PWAInstallInfo = {
    canInstall: false,
    isInstalled: false,
    isStandalone: false,
    platform: 'unknown',
  };
  installInstructions = '';

  ngOnInit(): void {
    // Aguardar um pouco antes de mostrar o banner
    setTimeout(() => {
      this.initializeBanner();
    }, 3000);

    // Observar mudanças no estado de instalação
    this.pwaService.installInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.installInfo = info;
        this.installInstructions = this.pwaService.getInstallInstructions();

        // Mostrar banner se pode instalar e não foi demitido
        if (info.canInstall && !this.wasDismissed()) {
          this.showBanner = true;
        }

        // Esconder banner se foi instalado
        if (info.isInstalled) {
          this.showBanner = false;
          this.showInstallSuccessToast();
        }
      });

    // Observar atualizações disponíveis
    this.pwaService.updateAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((available) => {
        this.updateAvailable = available;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeBanner(): void {
    // Verificar se já foi instalado ou banner foi dispensado
    if (this.installInfo.isInstalled || this.wasDismissed()) {
      return;
    }

    // Mostrar banner se pode instalar
    if (this.installInfo.canInstall) {
      this.showBanner = true;
    }
  }

  private wasDismissed(): boolean {
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000; // 1 semana em ms

    return dismissedTime > 0 && now - dismissedTime < oneWeek;
  }

  async installApp(): Promise<void> {
    this.isInstalling = true;

    try {
      const installed = await this.pwaService.installApp();

      if (installed) {
        this.showBanner = false;
        this.showInstallSuccessToast();
      }
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
    } finally {
      this.isInstalling = false;
    }
  }

  dismissBanner(): void {
    this.showBanner = false;
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  }

  private showInstallSuccessToast(): void {
    this.showInstallToast = true;

    // Auto-hide após 5 segundos
    setTimeout(() => {
      this.showInstallToast = false;
    }, 5000);
  }

  async updateApp(): Promise<void> {
    await this.pwaService.updateApp();
  }

  dismissUpdate(): void {
    this.updateAvailable = false;
  }
}
